import { ReactNode } from 'react';
import { useWatch } from 'react-hook-form';

import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { ModalFormLayout } from '@/components/form/layout/ModalFormLayout';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';

import { GraphVizNodeConfigFormField } from '../graphql/GraphVizNodeConfigFormFields';
import { GraphVizNodeComponentType } from '../graphVizNodeConfig';

interface GraphVizGroupBasicDetailsFormShape {
  displayName?: string | null;
  description?: string | null;
  applyToAllSections?: boolean;
  componentType: GraphVizNodeComponentType;
  hideGroupContents?: boolean;
}

interface GraphVizGroupBasicDetailsFormProps {
  showComponentType?: boolean;
  allowApplyToOtherSections?: boolean;
  autoFocus?: boolean;
  slots: {
    SelectTable: ReactNode;
    NotesField?: ReactNode;
  };
}

export function GraphVizGroupBasicDetailsForm<
  FormShape extends GraphVizGroupBasicDetailsFormShape,
>({
  slots,
  showComponentType = true,
  allowApplyToOtherSections = true,
  autoFocus = false,
}: GraphVizGroupBasicDetailsFormProps) {
  const { control } = useFormContext<FormShape>();
  const componentType = useWatch({ name: 'componentType' });

  return (
    <ModalFormLayout>
      {showComponentType && (
        <FormLayoutRow>
          <FormLayoutItem>
            <GraphVizNodeConfigFormField.ComponentType<FormShape>
              control={control}
              fieldName={'componentType' as const satisfies keyof FormShape}
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareTextInput<FormShape>
            control={control}
            required
            autoFocus={autoFocus}
            label="Group name"
            fieldName={'displayName' as const satisfies keyof FormShape}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem>
          {/* TODO / IMPORTANT NOTE: We utilize the currently unused "description" field on groups to generate notes
            This isn't great, ideally we could simultaneously create an EntityMapNodeConfiguration object at the time of GraphVizGroup creation,
            but the backend doesn't currently support that, so we're stuck with this until cascading updates are implemented in a custom mutation. */}
          {slots.NotesField ?? (
            <GraphVizNodeConfigFormField.Notes<FormShape>
              control={control}
              fieldName={'description' as const satisfies keyof FormShape}
            />
          )}
        </FormLayoutItem>
      </FormLayoutRow>
      {allowApplyToOtherSections && (
        <FormLayoutRow>
          <FormLayoutItem>
            <FormAwareCheckbox<FormShape>
              control={control}
              label="Apply group to other sections"
              helpText="If the same entities exist across other sections of this diagram, the same group will be created."
              fieldName={
                'applyToAllSections' as const satisfies keyof FormShape
              }
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
      {componentType !== GraphVizNodeComponentType.TileIcon && (
        <FormLayoutRow>
          <FormLayoutItem>
            <FormAwareCheckbox<FormShape>
              control={control}
              label="Hide list of group contents"
              helpText="When enabled, the group tile shows only the group name and value instead of its entities."
              fieldName={'hideGroupContents' as const satisfies keyof FormShape}
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
      {slots.SelectTable}
    </ModalFormLayout>
  );
}
